import React, { useEffect, useRef } from 'react';

/**
 * BaseModal - Reusable modal component
 */

const SIZE_MAP = {
  sm: '400px',
  md: '500px',
  lg: '800px',
  xl: '1200px',
};

const BaseModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
  headerStyle,
  headerIcon,
  subtitle,
  bodyStyle,
  zIndex = 1050,
  noPadding = false,
  fullHeight = false,
}) => {
  const overlayRef = useRef(null);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const maxWidth = SIZE_MAP[size] || SIZE_MAP.md;

  const defaultHeaderStyle = {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    border: '1px solid var(--primary)', color: 'var(--primary)' ,
    borderRadius: '8px 8px 0 0',
  };

  const mergedHeaderStyle = { ...defaultHeaderStyle, ...headerStyle };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
        overflow: 'auto',
      }}
    >
      <div
        className={className}
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)',
          width: '95%',
          maxWidth,
          maxHeight: '90vh',
          overflow: fullHeight ? 'hidden' : 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          fontSize: '12px',
          color: 'var(--text)',
          ...(fullHeight ? { display: 'flex', flexDirection: 'column', height: '90%' } : {}),
        }}
      >
        {/* Header */}
        <div style={mergedHeaderStyle}>
          {headerIcon && (
            <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{headerIcon}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h5 style={{ margin: 0, fontWeight: '600', fontSize: '13px' }}>
              {title}
            </h5>
            {subtitle && (
              <p style={{ margin: 0, fontSize: '10px', opacity: 0.9 }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              fontSize: '1.1rem',
              color: mergedHeaderStyle.color || 'var(--text-secondary)',
              padding: '2px 8px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: noPadding ? 0 : '1rem',
            ...(fullHeight ? { flex: 1, overflow: 'auto' } : {}),
            ...bodyStyle,
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: '0.75rem 1rem',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              backgroundColor: 'var(--bg)',
              flexShrink: 0,
              borderRadius: '0 0 8px 8px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseModal;
